import { Link } from 'react-router-dom'
import { useState } from 'react'
import { assetUrl } from '../utils/assetUrl'

interface ItemCardProps {
  id: string
  title: string
  image: string
  dailyPrice?: number
  salePrice?: number
  listingType: 'RENT' | 'SALE' | 'RENT_OR_SALE'
  condition: string
  location: string
  ownerName: string
  ownerAvatar?: string
  rating?: number
  reviewCount?: number
  isOwn?: boolean
}

export default function ItemCard({
  id,
  title,
  image,
  dailyPrice,
  salePrice,
  listingType,
  condition,
  location,
  ownerName,
  rating,
  reviewCount,
  isOwn,
}: ItemCardProps) {
  const [isFavorite, setIsFavorite] = useState(false)
  const [imgError, setImgError] = useState(false)

  const getBadges = () => {
    const badges = []
    if (listingType === 'RENT' || listingType === 'RENT_OR_SALE') {
      badges.push({ text: 'RENT', color: 'bg-[var(--forest-green)]' })
    }
    if (listingType === 'SALE' || listingType === 'RENT_OR_SALE') {
      badges.push({ text: 'SALE', color: 'bg-[var(--warning)]' })
    }
    return badges
  }

  const badges = getBadges()

  const hasReviews = typeof reviewCount === 'number' && reviewCount > 0 && typeof rating === 'number'

  const imageSection = (
    <div className="relative h-48 bg-[var(--bg-tint)] overflow-hidden flex items-center justify-center">
      {/* Placeholder Background - shown when no image */}
      {!image && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-[var(--forest-green)]/40">
          <svg className="w-14 h-14 mb-2" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.488.488 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84a.484.484 0 0 0-.48.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.488.488 0 0 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.63-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.27.41.48.41h3.84c.24 0 .44-.17.48-.41l-.36-2.54c-.59-.24-1.13-.56-1.62-.94l-2.39.96c-.22.08-.47 0-.59-.22l-1.92-3.32c-.12-.22-.07-.47.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
          </svg>
          <span className="text-sm font-medium">No image</span>
        </div>
      )}

      {/* Actual Image - only shown when an image URL is provided and loaded successfully */}
      {image && !imgError && (
        <img
          src={assetUrl(image)}
          alt={title}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-300 ${isOwn ? '' : 'group-hover:scale-105'}`}
          onLoad={() => {}}
          onError={() => setImgError(true)}
        />
      )}

      {/* Badges */}
      <div className="absolute top-3 left-3 flex gap-2">
        {badges.map((badge) => (
          <span
            key={badge.text}
            className={`${badge.color} text-white text-xs font-bold px-3 py-1 rounded-full`}
          >
            {badge.text}
          </span>
        ))}
        {isOwn && (
          <span className="bg-[var(--slate)] text-white text-xs font-bold px-3 py-1 rounded-full">
            Your listing
          </span>
        )}
      </div>

      {/* Favorite Button */}
      {!isOwn && (
        <button
          onClick={(e) => {
            e.preventDefault()
            setIsFavorite(!isFavorite)
          }}
          className="absolute top-3 right-3 w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-[var(--error)] hover:text-white transition-colors"
        >
          <svg
            className={`w-5 h-5 ${isFavorite ? 'fill-[var(--error)] text-[var(--error)]' : 'text-[var(--text-muted)]'}`}
            fill={isFavorite ? 'currentColor' : 'none'}
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        </button>
      )}

      {/* Condition Badge */}
      <div className="absolute bottom-3 left-3 bg-[var(--forest-green)] text-white px-2.5 py-1 rounded-full text-xs font-medium shadow-sm">
        {condition}
      </div>
    </div>
  )

  const contentSection = (
    <div className="p-4">
      <h3 className={`font-semibold text-[var(--text-primary)] mb-2 line-clamp-1 ${isOwn ? '' : 'group-hover:text-[var(--forest-green)] transition-colors'}`}>
        {title}
      </h3>

      {/* Location */}
      <div className="flex items-center text-sm text-[var(--text-muted)] mb-3">
        <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        </svg>
        <span className="truncate">{location}</span>
      </div>

      {/* Rating */}
      {rating !== undefined && (
        <div className="flex items-center gap-1 mb-3">
          {hasReviews ? (
            <>
              <span className="text-yellow-500">★</span>
              <span className="font-medium text-sm text-[var(--text-primary)]">{rating}</span>
              <span className="text-sm text-[var(--text-muted)]">({reviewCount})</span>
            </>
          ) : (
            <span className="text-xs text-[var(--text-muted)] italic">No reviews yet</span>
          )}
        </div>
      )}

      {/* Pricing */}
      <div className="flex items-end justify-between pt-3 border-t border-[var(--border)]">
        <div className="flex flex-col gap-1">
          {dailyPrice && (
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-bold text-[var(--forest-green)]">${dailyPrice}</span>
              <span className="text-sm text-[var(--text-muted)]">/day</span>
            </div>
          )}
          {salePrice && (
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-bold text-[var(--warning)]">${salePrice}</span>
              <span className="text-sm text-[var(--text-muted)]">sale</span>
            </div>
          )}
        </div>

        {/* Owner */}
        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
          <span className="truncate max-w-[100px]">{ownerName}</span>
        </div>
      </div>
    </div>
  )

  const cardClasses = `group bg-white rounded-xl overflow-hidden shadow-sm border border-[var(--border)] ${isOwn ? 'opacity-75 cursor-not-allowed' : 'hover:shadow-lg transition-all duration-300'}`

  if (isOwn) {
    return (
      <div className={cardClasses} title="This is your listing">
        {imageSection}
        {contentSection}
      </div>
    )
  }

  return (
    <div className={cardClasses}>
      <Link to={`/item/${id}`}>
        {imageSection}
        {contentSection}
      </Link>
    </div>
  )
}
